import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0118] to-[#1a0a2e] text-white p-6 sm:p-12">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6">
          <ArrowLeft size={20} />
          Back to Nexi
        </Link>
        
        <h1 className="text-3xl sm:text-4xl font-bold mb-2 bg-gradient-to-r from-purple-500 to-cyan-500 bg-clip-text text-transparent">
          Privacy Policy
        </h1>
        <p className="text-white/50 mb-8">Last updated: January 2025</p>

        <div className="space-y-8 text-white/80">
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Introduction</h2>
            <p>
              Nexi AI ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application ("App") and services. Please read this policy carefully. By using Nexi AI, you consent to the data practices described in this policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Information We Collect</h2>
            <p className="mb-3">We collect information that you provide directly to us:</p>
            
            <h3 className="text-lg font-medium text-white/90 mb-2">2.1 Personal Information</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Name:</strong> Your first name, used to personalize your experience and address you in conversations.</li>
              <li><strong>Consent Records:</strong> Records of your agreement to our Privacy Policy and Terms of Service.</li>
            </ul>
            
            <h3 className="text-lg font-medium text-white/90 mb-2">2.2 User-Generated Content</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Conversations:</strong> Chat messages you send to receive AI assistance. These are stored to maintain conversation history and provide context for better responses.</li>
              <li><strong>Images:</strong> Photos you upload for AI analysis. Images are processed in real-time and are NOT permanently stored on our servers after processing.</li>
            </ul>
            
            <h3 className="text-lg font-medium text-white/90 mb-2">2.3 Automatically Collected Information</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Device Information:</strong> Device type, operating system version, and unique device identifiers for app functionality.</li>
              <li><strong>Usage Data:</strong> Features used, time spent in the app, and interaction patterns to improve our services.</li>
              <li><strong>Local Storage:</strong> We store preferences and usage counters locally on your device using browser storage.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. How We Use Your Information</h2>
            <p className="mb-3">We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Provide AI Services:</strong> Process your requests and generate AI-powered responses.</li>
              <li><strong>Maintain Conversation Context:</strong> Store chat history to provide coherent, contextual responses across sessions.</li>
              <li><strong>Personalize Experience:</strong> Address you by name and tailor responses to your needs.</li>
              <li><strong>Improve Services:</strong> Analyze usage patterns to enhance app functionality and user experience.</li>
              <li><strong>Enforce Policies:</strong> Monitor for violations of our Terms of Service and content guidelines.</li>
              <li><strong>Communication:</strong> Send service-related notices and respond to your inquiries.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. Data Sharing and Disclosure</h2>
            <p className="mb-3">We may share your information in the following circumstances:</p>
            
            <h3 className="text-lg font-medium text-white/90 mb-2">4.1 Third-Party AI Services</h3>
            <p className="mb-4">
              We use Google's Gemini AI to process your requests. Your messages are sent to Google's servers for AI processing. Google has its own privacy policies governing this data. We recommend reviewing <a href="https://policies.google.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.
            </p>
            
            <h3 className="text-lg font-medium text-white/90 mb-2">4.2 Legal Requirements</h3>
            <p className="mb-4">
              We may disclose your information if required by law, court order, or governmental authority, or when we believe disclosure is necessary to protect our rights, your safety, or the safety of others.
            </p>
            
            <h3 className="text-lg font-medium text-white/90 mb-2">4.3 We Do NOT</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Sell your personal information to third parties</li>
              <li>Share your data with advertisers</li>
              <li>Use your data for targeted advertising</li>
              <li>Share your conversations with other users</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. Data Storage and Security</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Encryption:</strong> Data is encrypted in transit using TLS/SSL and at rest using industry-standard encryption.</li>
              <li><strong>Secure Infrastructure:</strong> Our servers are hosted on secure cloud infrastructure with access controls and monitoring.</li>
              <li><strong>Data Retention:</strong> Conversation history is retained until you delete it. You can delete conversations at any time through the app.</li>
              <li><strong>Image Processing:</strong> Uploaded images are processed in memory and are not stored permanently after analysis is complete.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Your Rights and Choices</h2>
            <p className="mb-3">You have the following rights regarding your data:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Deletion:</strong> Delete your conversation history at any time within the app. Request complete account deletion by contacting us.</li>
              <li><strong>Correction:</strong> Update or correct your personal information.</li>
              <li><strong>Portability:</strong> Export your conversation data in a machine-readable format.</li>
              <li><strong>Withdraw Consent:</strong> Stop using the app at any time. Previously collected data can be deleted upon request.</li>
              <li><strong>Opt-Out:</strong> Clear local storage in your browser/device settings to reset usage data.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Children's Privacy</h2>
            <p>
              Nexi AI is not intended for use by children under 13 years of age. We do not knowingly collect personal information from children under 13. If we learn we have collected personal information from a child under 13, we will delete that information promptly. If you believe we may have collected information from a child under 13, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. International Data Transfers</h2>
            <p>
              Your information may be transferred to, stored, and processed in countries other than your country of residence, including the United States. These countries may have data protection laws that are different from the laws of your country. By using Nexi AI, you consent to the transfer of your information to these countries.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Third-Party Links</h2>
            <p>
              The App may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to read the privacy policies of any third-party services you access.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of significant changes by posting a notice in the App or updating the "Last updated" date. Your continued use of Nexi AI after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. California Privacy Rights (CCPA)</h2>
            <p className="mb-3">If you are a California resident, you have additional rights:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Right to know what personal information is collected</li>
              <li>Right to know if personal information is sold or disclosed</li>
              <li>Right to opt-out of the sale of personal information (we do not sell your data)</li>
              <li>Right to non-discrimination for exercising your privacy rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">12. Contact Us</h2>
            <p className="mb-3">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:
            </p>
            <div className="bg-black/30 p-4 rounded-lg">
              <p><strong>Email:</strong> support@nexi-ai.net</p>
              <p><strong>Website:</strong> https://nexi-ai.net</p>
            </div>
            <p className="mt-3 text-white/60 text-sm">
              We will respond to your inquiry within 30 days.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 text-center text-white/50">
          <p>&copy; 2025 Nexi AI. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
